/**
 * Feature Flag Interface
 */
export type FeatureFlag = {
  /** Unique identifier for the feature flag */
  id: string;
  /** Whether the feature flag is enabled */
  enabled: boolean;
};

/**
 * Feature Flag Keys
 */
export const FEATURE_FLAGS = {
  FEATURE_FLAG_BACKEND_POOL_TX_HISTORY_API: 'FEATURE_FLAG_BACKEND_POOL_TX_HISTORY_API',
  FEATURE_FLAG_BACKEND_POOL_VOLUME_HISTORY_API: 'FEATURE_FLAG_BACKEND_POOL_VOLUME_HISTORY_API',
  FEATURE_FLAG_BACKEND_USE_STORED_PROCEDURES_FOR_VOLUME_AND_FEES:
    'FEATURE_FLAG_BACKEND_USE_STORED_PROCEDURES_FOR_VOLUME_AND_FEES',
  NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_POOL_DETAILS_PAGE:
    'NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_POOL_DETAILS_PAGE',
  NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_USDC_REWARDS: 'NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_USDC_REWARDS',
  NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_TOKENS:
    'NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_TOKENS',
  NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_FRONTEND:
    'NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_FRONTEND',
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

/**
 * Initialize feature flags with all flags disabled by default
 * Values will be overridden by environment variables
 */
export const featureFlags: Record<string, FeatureFlag> = Object.keys(FEATURE_FLAGS).reduce(
  (flags, key) => {
    flags[key] = {
      id: key,
      enabled: false, // Default to disabled, will be overridden by environment variables
    };
    return flags;
  },
  {} as Record<string, FeatureFlag>,
);

export function getFeatureFlagsFromEnv(): Record<string, FeatureFlag> {
  if (typeof process === 'undefined') return featureFlags;

  // note that nextjs does not support dynamic queries in process.env
  // e.g process.env[featureFlag]
  // so we need to hardcode the variable names here
  return {
    FEATURE_FLAG_BACKEND_POOL_TX_HISTORY_API: {
      id: FEATURE_FLAGS.FEATURE_FLAG_BACKEND_POOL_TX_HISTORY_API,
      enabled: process.env.FEATURE_FLAG_BACKEND_POOL_TX_HISTORY_API === 'true',
    },
    FEATURE_FLAG_BACKEND_POOL_VOLUME_HISTORY_API: {
      id: FEATURE_FLAGS.FEATURE_FLAG_BACKEND_POOL_VOLUME_HISTORY_API,
      enabled: process.env.FEATURE_FLAG_BACKEND_POOL_VOLUME_HISTORY_API === 'true',
    },
    FEATURE_FLAG_BACKEND_USE_STORED_PROCEDURES_FOR_VOLUME_AND_FEES: {
      id: FEATURE_FLAGS.FEATURE_FLAG_BACKEND_USE_STORED_PROCEDURES_FOR_VOLUME_AND_FEES,
      enabled:
        process.env.FEATURE_FLAG_BACKEND_USE_STORED_PROCEDURES_FOR_VOLUME_AND_FEES === 'true',
    },
    NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_POOL_DETAILS_PAGE: {
      id: FEATURE_FLAGS.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_POOL_DETAILS_PAGE,
      enabled: process.env.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_POOL_DETAILS_PAGE === 'true',
    },
    NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_USDC_REWARDS: {
      id: FEATURE_FLAGS.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_USDC_REWARDS,
      enabled: process.env.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_USDC_REWARDS === 'true',
    },
    NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_TOKENS: {
      id: FEATURE_FLAGS.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_TOKENS,
      enabled: process.env.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_TOKENS === 'true',
    },
    NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_FRONTEND: {
      id: FEATURE_FLAGS.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_FRONTEND,
      enabled: process.env.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_FRONTEND === 'true',
    },
  };
}
